/* eslint-disable */
// Copied from _badges/system/src/compose.js by scripts/sync-badge-art.mjs. Do not edit here:
// change the badge system, then run `pnpm badges:sync-art`.
// Badge composer. Pure functions, no Node or browser APIs, so the same code runs in
// the build script, the CLI and the preview playground.
//
//   composeFull(spec, lib) -> SVG string on the 384 canvas
//   composeMini(spec, lib) -> SVG string on the 32 canvas
//
// spec: { code, tier, number, category, layers?, repeat?, miniGlyph? }
//   layers defaults to the category's layers from categories.json.
// lib:  { tokens, manifest, categories, parts: { id: { viewBox, body } }, faces: { face: json } }
//   built by src/library.mjs (Node) or loaded from preview/lib.json (browser).
//
// Animation hooks: the root <svg> has class="bdg" and every layer group has a class
// (bdg-glow, bdg-honor, bdg-base, bdg-numeral, bdg-modifier, bdg-tool, bdg-tool-front,
// bdg-counter; the mini adds bdg-glyph). A classed group never carries a transform
// attribute, so CSS can transform it without moving the drawing. The site animates them.

const r2 = (n) => Math.round(n * 100) / 100;

export function resolveSpec(input, lib) {
  const cat = lib.categories[input.category];
  if (!cat) throw new Error(`unknown category ${input.category}`);
  const tier = lib.tokens.tiers[input.tier];
  if (!tier) throw new Error(`unknown tier ${input.tier}`);
  return {
    code: input.code || `${input.category}_${input.number}`,
    category: input.category,
    tier: input.tier,
    number: String(input.number ?? ''),
    repeat: input.repeat ?? null,
    layers: { ...cat.layers, ...(input.layers || {}) },
    miniGlyph: input.miniGlyph || cat.miniGlyph,
    // "year": the number is a year, and the mini shows its last two digits ("25", not "2.0K")
    miniNumber: cat.miniNumber || null,
  };
}

// ---------- part helpers ----------

function part(lib, id) {
  const p = lib.parts[id];
  if (!p) throw new Error(`unknown part ${id}`);
  return p;
}

// Apply tier colour roles (data-role="fill:gloss stroke:fill") to a part body.
function recolour(body, colours) {
  return body.replace(/<(\w+)([^>]*?)data-role="([^"]+)"([^>]*)\/>/g, (m, tag, a, roles, b) => {
    let attrs = `${a}${b}`;
    for (const role of roles.split(/\s+/)) {
      const [prop, name] = role.split(':');
      if (!colours[name]) continue;
      attrs = attrs.match(new RegExp(`\\s${prop}="`))
        ? attrs.replace(new RegExp(`\\s${prop}="[^"]*"`), ` ${prop}="${colours[name]}"`)
        : `${attrs} ${prop}="${colours[name]}"`;
    }
    return `<${tag}${attrs.replace(/\s+$/, '')}/>`;
  });
}

function layerElements(body, layer) {
  return (body.match(new RegExp(`<\\w+[^>]*data-layer="${layer}"[^>]*/>`, 'g')) || []);
}

const stripMeta = (s) => s.replace(/\s(data-role|data-layer)="[^"]*"/g, '');

// ---------- geometry of the badge ----------

// Where the base sits and how big it is for a tier.
function baseFrame(lib, tierName) {
  const { tokens } = lib;
  const tier = tokens.tiers[tierName];
  const p = part(lib, tier.base);
  const [vx, vy, w, h] = p.viewBox;
  const scale = tier.size / Math.max(w, h);
  const [cx, cy] = tokens.layout.center;
  const meta = lib.manifest[tier.base] || {};
  const [ox, oy] = meta.center || [vx + w / 2, vy + h / 2];
  return { tier, part: p, meta, scale, cx, cy, w, h, ox, oy, radius: (Math.max(w, h) * scale) / 2 };
}

// A part may override its home slot's geometry: compact objects (a cup, a trophy)
// sit on the edge at about 40 degrees instead of hanging off it like a long brush.
// The override only applies in the slot that matches the part's kind, so a cup
// borrowed as an honor still goes where honors go.
const HOME_SLOT = { tool: 'tool', modifier: 'modifier', honor: 'honor' };
function slotPoint(lib, frame, slotName, partId) {
  const meta = (partId && lib.manifest[partId]) || {};
  const own = meta.placement && HOME_SLOT[meta.kind] === slotName ? meta.placement : {};
  const slot = { ...lib.tokens.layout.slots[slotName], ...own };
  const a = (slot.angle * Math.PI) / 180;
  const d = frame.radius * slot.reach;
  return { x: frame.cx + Math.cos(a) * d + (slot.dx || 0), y: frame.cy + Math.sin(a) * d + (slot.dy || 0), slot };
}

function placePart(lib, id, at, extraScale = 1) {
  const p = part(lib, id);
  const meta = lib.manifest[id] || {};
  const [vx, vy, w, h] = p.viewBox;
  const [ax, ay] = meta.anchor || [vx + w / 2, vy + h / 2];
  const s = (meta.scale ?? 1) * (at.slot?.scale ?? 1) * extraScale;
  return `<g transform="translate(${r2(at.x)} ${r2(at.y)}) scale(${r2(s)}) translate(${r2(-ax)} ${r2(-ay)})">${stripMeta(p.body)}</g>`;
}

// ---------- numerals ----------

function textRun(face, text, tracking) {
  let x = 0;
  const glyphs = [];
  let minY = Infinity, maxY = -Infinity, minX = Infinity, maxX = -Infinity;
  for (const ch of text) {
    const g = face.glyphs[ch];
    if (!g) throw new Error(`face ${face.face} has no glyph "${ch}"`);
    glyphs.push({ d: g.d, x });
    if (g.box[2] > g.box[0]) {
      minX = Math.min(minX, x + g.box[0]); maxX = Math.max(maxX, x + g.box[2]);
      minY = Math.min(minY, g.box[1]); maxY = Math.max(maxY, g.box[3]);
    }
    x += g.adv + tracking;
  }
  return { glyphs, box: { x: minX, y: minY, w: maxX - minX, h: maxY - minY } };
}

// How a number breaks into rows. A numeral style may override per length,
// e.g. the pixel style stacks three digits in a column: { "3": [1, 1, 1] }.
export function numberLines(number, layouts = {}) {
  const s = String(number);
  const plan = layouts[s.length] || (s.length <= 3 ? [s.length] : [Math.ceil(s.length / 2), Math.floor(s.length / 2)]);
  const out = [];
  let i = 0;
  for (const n of plan) { out.push(s.slice(i, i + n)); i += n; }
  return out;
}

// Returns { defs, body } for the numeral of a badge, fitted into the base's numeral box.
// With a modifier on the lower left, the box gives up its left edge (tokens.layout.numeralYield)
// so "#" and "10" sit side by side instead of on top of each other.
function numeral(lib, frame, number, idp, hasModifier) {
  const style = lib.manifest[frame.tier.numeral];
  const face = lib.faces[style.face];
  const lines = numberLines(number, style.layouts).map((t) => textRun(face, t, style.tracking || 0));
  const lineGap = (style.lineGap ?? 0.12) * 1000;
  const blockW = Math.max(...lines.map((l) => l.box.w));
  const lineH = Math.max(...lines.map((l) => l.box.h));
  const blockH = lineH * lines.length + lineGap * (lines.length - 1);
  // Numeral box is given in base-part coordinates; convert to canvas.
  const [bx, by, bw, bh] = frame.meta.numeralBox || [frame.part.viewBox[0] + frame.w * 0.2, frame.part.viewBox[1] + frame.h * 0.25, frame.w * 0.6, frame.h * 0.5];
  const box = {
    x: frame.cx + (bx - frame.ox) * frame.scale, y: frame.cy + (by - frame.oy) * frame.scale,
    w: bw * frame.scale, h: bh * frame.scale,
  };
  const yieldX = hasModifier ? (lib.tokens.layout.numeralYield?.modifier ?? 0) * box.w : 0;
  box.x += yieldX; box.w -= yieldX;
  const maxH = box.h * (lines.length > 1 ? 1 : (style.maxHeight ?? 1));
  const s = Math.min(box.w / blockW, maxH / blockH);
  const ox = box.x + box.w / 2, oy = box.y + box.h / 2 + (style.nudgeY || 0) * box.h;
  let d = '';
  lines.forEach((l, i) => {
    const lx = -l.box.w / 2 - l.box.x;
    const ly = -blockH / 2 + i * (lineH + lineGap) - l.box.y + (lineH - l.box.h);
    for (const g of l.glyphs) d += `<path transform="translate(${r2(lx + g.x)} ${r2(ly)})" d="${g.d}"/>`;
  });
  const place = `translate(${r2(ox)} ${r2(oy)}) scale(${r2(s * 1000) / 1000})`;
  const id = `${idp}-num`;
  const defs = [`<g id="${id}">${d}</g>`];
  const out = [];
  const u = 1 / s; // one canvas unit in glyph space
  if (style.glow) {
    defs.push(`<filter id="${idp}-numglow" x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="${r2(style.glow.blur * u)}"/></filter>`);
    out.push(`<use href="#${id}" fill="${style.glow.color}" opacity="${style.glow.opacity ?? 1}" stroke="${style.glow.color}" stroke-width="${r2((style.glow.spread || 0) * u)}" filter="url(#${idp}-numglow)"/>`);
  }
  if (style.shadow) {
    defs.push(`<filter id="${idp}-numshadow" x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="${r2(style.shadow.blur * u)}"/></filter>`);
    out.push(`<use href="#${id}" x="${r2(style.shadow.dx * u)}" y="${r2(style.shadow.dy * u)}" fill="${style.shadow.color}" opacity="${style.shadow.opacity ?? 1}" filter="url(#${idp}-numshadow)"/>`);
  }
  if (style.extrude) {
    const { dx, dy, steps, color } = style.extrude;
    for (let k = steps; k >= 1; k--) {
      out.push(`<use href="#${id}" x="${r2((dx * k * u) / steps)}" y="${r2((dy * k * u) / steps)}" fill="${color}" stroke="${color}" stroke-width="${r2(u * 1.5)}"/>`);
    }
  }
  const stroke = style.stroke ? ` stroke="${style.stroke.color}" stroke-width="${r2(style.stroke.width * u)}" stroke-linejoin="${style.stroke.join || 'miter'}"${style.stroke.under ? ' paint-order="stroke"' : ''}` : '';
  out.push(`<use href="#${id}" fill="${style.fill}"${stroke}/>`);
  return { defs: defs.join(''), body: `<g transform="${place}">${out.join('')}</g>`, defsTransform: place };
}

// ---------- full badge ----------

export function composeFull(input, lib) {
  const spec = resolveSpec(input, lib);
  const { tokens } = lib;
  const N = tokens.canvas;
  const idp = spec.code.replace(/[^A-Za-z0-9_-]/g, '_');
  const frame = baseFrame(lib, spec.tier);
  const colours = frame.tier.colors || {};
  const defs = [];
  const groups = [];
  const baseT = `translate(${r2(frame.cx)} ${r2(frame.cy)}) scale(${r2(frame.scale * 1000) / 1000}) translate(${r2(-frame.ox)} ${r2(-frame.oy)})`;

  // glow: a blurred copy of the base silhouette in the tier's glow colour
  if (frame.tier.glow) {
    const sil = layerElements(frame.part.body, 'silhouette').map((e) => stripMeta(e).replace(/fill="[^"]*"/, `fill="${frame.tier.glow.color}"`)).join('');
    defs.push(`<filter id="${idp}-glowblur" x="-40%" y="-40%" width="180%" height="180%"><feGaussianBlur stdDeviation="${r2(frame.tier.glow.blur / frame.scale)}"/></filter>`);
    groups.push(`<g id="${idp}-glow" class="bdg-glow" opacity="${frame.tier.glow.opacity}"><g transform="${baseT}"><g filter="url(#${idp}-glowblur)" transform="translate(${r2(frame.ox)} ${r2(frame.oy)}) scale(${frame.tier.glow.grow}) translate(${r2(-frame.ox)} ${r2(-frame.oy)})">${sil}</g></g></g>`);
  }

  const at = (slotName, partId) => slotPoint(lib, frame, slotName, partId);
  const L = spec.layers;
  if (L.honor) groups.push(`<g id="${idp}-honor" class="bdg-honor">${placePart(lib, L.honor, at('honor', L.honor))}</g>`);
  groups.push(`<g id="${idp}-base" class="bdg-base"><g transform="${baseT}">${stripMeta(recolour(frame.part.body, colours))}</g></g>`);
  if (spec.number) {
    const n = numeral(lib, frame, spec.number, idp, !!L.modifier);
    defs.push(n.defs);
    groups.push(`<g id="${idp}-numeral" class="bdg-numeral">${n.body}</g>`);
  }
  if (L.modifier) groups.push(`<g id="${idp}-modifier" class="bdg-modifier">${placePart(lib, L.modifier, at('modifier', L.modifier))}</g>`);
  if (L.tool) groups.push(`<g id="${idp}-tool" class="bdg-tool">${placePart(lib, L.tool, at('tool', L.tool))}</g>`);
  if (L.toolFront) groups.push(`<g id="${idp}-tool-front" class="bdg-tool-front">${placePart(lib, L.toolFront, at('toolFront', L.toolFront))}</g>`);
  if (spec.repeat && spec.repeat > 1) groups.push(`<g id="${idp}-counter" class="bdg-counter">${counterChipFull(lib, at('counter'), spec.repeat)}</g>`);

  return `<svg xmlns="http://www.w3.org/2000/svg" class="bdg" viewBox="0 0 ${N} ${N}" width="${N}" height="${N}" data-badge="${spec.code}" data-tier="${spec.tier}">`
    + `<title>${spec.code}</title><defs>${defs.join('')}</defs>${groups.join('')}</svg>\n`;
}

// ---------- counter chip ("x3") ----------

// Full size: Press Start 2P digits (the pixel voice of the diamond numbers) on a white pill.
function counterChipFull(lib, at, count) {
  const face = lib.faces.pixel;
  const run = textRun(face, `x${count}`, 40);
  const h = lib.tokens.layout.slots.counter.textHeight ?? 26;
  const s = h / run.box.h;
  const tw = run.box.w * s, padX = 12, padY = 9;
  const w = tw + padX * 2, hh = h + padY * 2;
  const sw = lib.tokens.stroke.outline;
  const glyphs = run.glyphs.map((g) => `<path transform="translate(${r2(g.x - run.box.x)} ${r2(-run.box.y)})" d="${g.d}"/>`).join('');
  return `<g transform="translate(${r2(at.x - w / 2)} ${r2(at.y - hh / 2)})"><rect width="${r2(w)}" height="${r2(hh)}" rx="${r2(hh / 2)}" fill="#fff" stroke="#000" stroke-width="${sw}"/><g transform="translate(${padX} ${padY}) scale(${r2(s * 1000) / 1000})" fill="#000">${glyphs}</g></g>`;
}

// Mini size: the 3 x 5 pixel digits.

function counterChip(lib, at, count, scale) {
  const cell = 5 * scale;
  const { d, width: tw } = pixelPath(lib.tokens.miniDigits, `x${count}`, cell);
  const th = 5 * cell;
  const padX = 9 * scale, padY = 7 * scale, w = tw + padX * 2, h = th + padY * 2;
  // The outline stays at the badge's normal weight however big the chip is drawn.
  const sw = Math.min(lib.tokens.stroke.outline, lib.tokens.stroke.outline * scale);
  return `<g transform="translate(${r2(at.x - w / 2)} ${r2(at.y - h / 2)})"><rect x="0" y="0" width="${r2(w)}" height="${r2(h)}" rx="${r2(h / 2)}" fill="#fff" stroke="#000" stroke-width="${sw}"/><path transform="translate(${r2(padX)} ${r2(padY)})" d="${d}" fill="#000"/></g>`;
}

// ---------- pixel digits (mini numbers and the counter chip) ----------

export function pixelPath(font, text, cell) {
  let x = 0, d = '';
  for (const ch of text) {
    const g = font.glyphs[ch];
    if (!g) throw new Error(`mini digits have no "${ch}"`);
    g.rows.forEach((row, yy) => [...row].forEach((c, xx) => { if (c === '#') d += `M${r2(x + xx * cell)} ${r2(yy * cell)}h${cell}v${cell}h${-cell}Z`; }));
    x += (g.rows[0].length + 1) * cell;
  }
  return { d, width: x - cell };
}

// ---------- mini badge ----------

export function miniText(number, mode = null) {
  const n = Number(number);
  if (mode === 'year' && Number.isInteger(n) && n >= 1000) return String(n % 100).padStart(2, '0');
  if (!Number.isFinite(n) || n < 1000) return String(number);
  const k = n / 1000;
  return `${Number.isInteger(k) ? k : k.toFixed(1)}K`;
}

export function composeMini(input, lib) {
  const spec = resolveSpec(input, lib);
  const { tokens } = lib;
  const M = tokens.mini;
  const tier = tokens.tiers[spec.tier];
  const p = part(lib, tier.base);
  const [vx, vy, w, h] = p.viewBox;
  const meta = lib.manifest[tier.base] || {};
  const size = tier.miniSize;
  const s = size / Math.max(w, h);
  const [ox, oy] = meta.center || [vx + w / 2, vy + h / 2];
  const [cx, cy] = M.center;
  const idp = `${spec.code.replace(/[^A-Za-z0-9_-]/g, '_')}-mini`;
  const colours = tier.colors || {};
  const sw = M.outline / s;
  const sil = layerElements(recolour(p.body, colours), 'silhouette').map((e) => stripMeta(e).replace(/\/>$/, ` stroke="#000" stroke-width="${r2(sw)}" stroke-linejoin="round"/>`)).join('');
  const gloss = layerElements(recolour(p.body, colours), 'gloss').map((e) => stripMeta(e).replace(/\sstroke(-width|-miterlimit)?="[^"]*"/g, '')).join('');
  const baseT = `translate(${cx} ${cy}) scale(${r2(s * 10000) / 10000}) translate(${r2(-ox)} ${r2(-oy)})`;
  const clip = `<clipPath id="${idp}-clip">${layerElements(p.body, 'silhouette').map(stripMeta).join('')}</clipPath>`;
  const out = [];
  out.push(`<g class="bdg-base"><g id="${idp}-base" transform="${baseT}">${sil}<g clip-path="url(#${idp}-clip)">${gloss}</g></g></g>`);

  // number in the mini pixel digits
  const text = miniText(spec.number, spec.miniNumber);
  if (text) {
    const px = tokens.miniDigits;
    const cell = text.length <= 3 ? M.digitCell : M.digitCellSmall;
    const { d, width: tw } = pixelPath(tokens.miniDigits, text, cell);
    const th = 5 * cell;
    const [nx, ny] = M.numberCenter;
    const nc = tier.miniNumber;
    // One path, outline painted under the fill, so neighbouring pixels do not show seams.
    out.push(`<g class="bdg-numeral"><path id="${idp}-numeral" transform="translate(${r2(nx - tw / 2)} ${r2(ny - th / 2)})" d="${d}" fill="${nc.fill}" stroke="${nc.outline}" stroke-width="${M.digitOutline}" stroke-linejoin="round" paint-order="stroke"/></g>`);
  }

  // one glyph: the category's mini glyph, bottom right
  if (spec.miniGlyph) {
    // Mini glyphs are separate drawings (<part>.mini.svg), not scaled-down full parts.
    const g = part(lib, `${spec.miniGlyph}:mini`);
    const [gx, gy, gw, gh] = g.viewBox;
    const gs = M.glyphSize / Math.max(gw, gh);
    const [ax, ay] = [gx + gw / 2, gy + gh / 2];
    out.push(`<g class="bdg-glyph"><g id="${idp}-glyph" transform="translate(${M.glyphAt[0]} ${M.glyphAt[1]}) scale(${r2(gs * 1000) / 1000}) translate(${r2(-ax)} ${r2(-ay)})">${stripMeta(g.body)}</g></g>`);
  }
  if (spec.repeat && spec.repeat > 1) out.push(`<g id="${idp}-counter" class="bdg-counter">${counterChip(lib, { x: M.counterAt[0], y: M.counterAt[1] }, spec.repeat, M.counterScale)}</g>`);

  return `<svg xmlns="http://www.w3.org/2000/svg" class="bdg" viewBox="0 0 ${M.canvas} ${M.canvas}" width="${M.canvas}" height="${M.canvas}" data-badge="${spec.code}" data-tier="${spec.tier}" data-size="mini">`
    + `<title>${spec.code}</title><defs>${clip}</defs>${out.join('')}</svg>\n`;
}
